import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, NavigationEnd, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthServiceService } from './services/auth-service/auth-service.service';
import { ModuleEnum } from './enum/module-enum';
import { UserAccountAndAccessLevelService } from './services/user-account-and-access-level/user-account-and-access-level.service';
import { AccessLevelModel } from './models/access-level/access-level';
import { CommonServiceService } from './services/common-service/common-service.service';
import { HttpErrorResponse } from '@angular/common/http';
import swal from 'sweetalert2';

const DEACTIVATED_ACCOUNT_MESSAGE = 'Your account has been deactivated. Please contact manager for further assistance. Thank You!';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  accessLevel: AccessLevelModel | null = null;
  accessLevelLoaded: boolean = false;
  constructor(
    private authService: AuthServiceService,
    private router: Router,
    private userAccountAndAccessLevelService: UserAccountAndAccessLevelService,
    private commonService: CommonServiceService) {
  }
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const pageModule: ModuleEnum = route.data['pageModule'];
    if (state.url.includes('login')) {
      if (!this.authService.isLoggedIn()) {
        return true;
      }

      return this.fetchAccessLevelByUserAccountId().then(() => {
        this.router.navigateByUrl('');
        return false;
      }).catch(() => {
        return true;
      });
    }
    else {
      if (this.authService.isLoggedIn()) {
        return this.fetchAccessLevelByUserAccountId().then(() => {
          return this.checkAccess(pageModule, state.url);
        }).catch(() => {
          return false;
        });
      } else {
        this.router.navigateByUrl('login');
        return false;
      }
    }
  }

  checkAccess(pageModule: ModuleEnum, urlThatTryingToAccess: string): boolean {
    this.commonService.changeAccessLevel(this.accessLevel);
    if (this.validateByPageModuleAndAccessLevel(pageModule, urlThatTryingToAccess)) {
      return true;
    } else {
      swal.fire('Access Denied', 'You don\'t have permission to access this page', 'error');
      this.router.navigateByUrl('');
      return false;
    }
  }

  validateByPageModuleAndAccessLevel(pageModule: ModuleEnum, urlThatTryingToAccess: string): boolean {
    let isAllowedView: boolean = false;
    switch (pageModule) {
      case ModuleEnum.ACTIVITY: {
        if (this.accessLevel?.activityView) isAllowedView = true;
        break;
      }
      case ModuleEnum.BANNER: {
        if (this.accessLevel?.bannerView) isAllowedView = true;
        break;
      }
      case ModuleEnum.DASHBOARD: {
        isAllowedView = true;
        break;
      }
      case ModuleEnum.NAV_LAYER: {
        if (this.accessLevel?.navLayerView) isAllowedView = true;
        break;
      }
      case ModuleEnum.NAV_PAGE: {
        if (urlThatTryingToAccess.includes('page-editor-list')) {
          isAllowedView = this.accessLevel?.navPageEditorView;
        } else {
          isAllowedView = this.accessLevel?.navPageView;
        }

        break;
      }
      case ModuleEnum.NAV_ALBUM: {
        if (urlThatTryingToAccess.includes('album-item-list')) {
          isAllowedView = this.accessLevel?.navAlbumItemView;
        } else {
          isAllowedView = this.accessLevel?.navAlbumView;
        }
        break;
      }
      case ModuleEnum.PROFILE_SETTING: {
        isAllowedView = true;
        break;
      }
      case ModuleEnum.USER_ACCOUNT: {
        if (urlThatTryingToAccess.includes('user-account-type-list')) {
          isAllowedView = this.accessLevel?.userAccountTypeView;
        } else {
          isAllowedView = this.accessLevel?.userAccountView;
        }
        break;
      }
      case ModuleEnum.VENUE: {
        if (urlThatTryingToAccess.includes('venue-booking-list')) {
          isAllowedView = this.accessLevel?.venueBookingView;
        } else {
          isAllowedView = this.accessLevel?.venueView;
        }
        break;
      }
      case ModuleEnum.SHORTEN_URL: {
        if (this.accessLevel?.navLayerView) isAllowedView = true;
        break;
      }
      case ModuleEnum.DONATION: {
        if (this.accessLevel?.donationView) isAllowedView = true;
        break;
      }
      case ModuleEnum.LEARNING: {
        isAllowedView = true;
        // if (this.accessLevel?.dharmaHubView) {
        //   isAllowedView = true;
        // } else {
        //   isAllowedView = this.accessLevel?.videoPodcastView;
        // }

        break;
      }
    }
    return isAllowedView;
  }
  fetchAccessLevelByUserAccountId(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.userAccountAndAccessLevelService.getAccessLevelByUserAccountId().subscribe({
        next: (data) => {
          if (!this.authService.isCmsAccessAllowed(data.userAccount)) {
            this.commonService.changeAccessLevel(null);
            this.authService.invalidateSession('Access Revoked', DEACTIVATED_ACCOUNT_MESSAGE);
            reject(new Error('CMS access revoked'));
            return;
          }

          this.accessLevel = data.accessLevel;
          this.commonService.changeAccessLevel(this.accessLevel);
          this.accessLevelLoaded = true;
          resolve();
        }, error: (error: HttpErrorResponse) => {
          this.commonService.changeAccessLevel(null);

          if (error.status == 401 || error.status == 403) {
            this.authService.invalidateSession(
              'Auto Logged Out',
              'You have been logged out because of invalid login credentials',
            );
          }

          reject(error);
        }
      })
    });
  }
}
